import { useAtom } from "jotai";
import React, { useState } from "react";
import CustomButton, {
  BtnRadius,
  BtnSizes,
  BtnTypes,
} from "@/app/components/lib/CustomButton";
import NextImage from "@/app/components/lib/NextImage";
import Price from "@/app/components/lib/Price";
import { exchangeAtom } from "@/hooks/exchangeAtom";
import { useTranslation } from "@/hooks/localization/useTranslations";
import { Col, Modal, Row } from "@/utils/bootstrap";
import { getExchangeTotals } from "@/utils/helpers/exchange-totals";
import { getTaxLabelKey } from "@/utils/helpers/tax";
import SvgRemoveFromCart from "../all-cart-items/cart-item/cart-item-btns/item-romove-btn/SvgRemoveFromCart";

interface Props {
  mobile?: boolean;
}

const CartExchangeSection: React.FC<Props> = ({ mobile }) => {
  const [exchangeData, setExchangeData] = useAtom(exchangeAtom);
  const t = useTranslation();
  const [showCancelModal, setShowCancelModal] = useState(false);

  if (!exchangeData || exchangeData.items.length === 0) return null;

  const { subtotal: totalExchangeValue, tax: totalExchangeTax, shipping, credit } =
    getExchangeTotals(exchangeData);
  const taxLabel = t[getTaxLabelKey(exchangeData.tax_mode)];

  const handleCancelExchange = () => {
    setExchangeData(null);
    setShowCancelModal(false);
  };

  return (
    <>
      <div className={`cart-exchange-section mb-4 ${mobile ? "mobile" : ""}`}>
        <div className="section-header">
          <h5>
            {t.exchange_items} ({exchangeData.items.length})
          </h5>
          <button type="button" className="cancel-link border-0 bg-transparent p-0" onClick={() => setShowCancelModal(true)}>
            {t.cancel_exchange}
          </button>
        </div>

        <div className="exchange-items-list">
          {exchangeData.items.map((item) => (
            <div className="exchange-item-card" key={item.id}>
              <div className="item-image-container">
                <NextImage
                  src={item.thumbnail}
                  alt={item.name}
                  layout="fill"
                  objectFit="cover"
                  sizes="64px"
                  width={64}
                  height={64}
                />
              </div>

              <div className="item-content">
                <div className="item-top-row">
                  <div className="item-name">{item.name}</div>
                  <div className="item-price">
                    <Price
                      forcedCurrency={exchangeData.currency}
                      price={{ value: item.price * item.quantity }}
                    />
                  </div>
                </div>

                <div className="item-meta">
                  <div className="d-flex gap-2">
                    {t.qty || "Qty"}: {item.quantity}
                  </div>
                  {exchangeData.enable_taxes !== false && !!item.tax && (
                    <div className="d-flex gap-1">
                      {taxLabel}:{" "}
                      <Price
                        forcedCurrency={exchangeData.currency}
                        price={{ value: item.tax * item.quantity }}
                      />
                    </div>
                  )}
                </div>

                <div className="item-reason-row">
                  <div className="reason-block">
                    <div className="reason-label">{t.reason_for_exchange}</div>
                    <div className="reason-value">{item.note || "-"}</div>
                  </div>
                  <button
                    type="button"
                    aria-label={`${t.remove_item}: ${item.name}`}
                    className="delete-btn border-0 bg-transparent p-0"
                    onClick={() => {
                      const newItems = exchangeData.items.filter(
                        (i) => i.id !== item.id,
                      );
                      if (newItems.length === 0) {
                        setExchangeData(null);
                      } else {
                        setExchangeData({ ...exchangeData, items: newItems, shippingCharge: 0 });
                      }
                    }}
                  >
                    <span className="text-danger">
                      <SvgRemoveFromCart className="fill-current" />
                    </span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {totalExchangeTax > 0 && (
          <>
            <div
              className="total-section"
              style={{ borderBottom: "none", paddingBottom: 0 }}
            >
              <span className="text-muted">{t.sub_total}</span>
              <span className="total-value-text text-muted">
                <Price
                  forcedCurrency={exchangeData.currency}
                  price={{ value: totalExchangeValue }}
                />
              </span>
            </div>
            <div
              className="total-section pt-2"
              style={{ borderBottom: "none", paddingBottom: 0 }}
            >
              <span className="text-muted">{taxLabel}</span>
              <span className="total-value-text text-muted">
                <Price
                  forcedCurrency={exchangeData.currency}
                  price={{ value: totalExchangeTax }}
                />
              </span>
            </div>
          </>
        )}
        {shipping > 0 && (
          <div className="total-section pt-2">
            <span>{t.returned_shipping_charge}</span>
            <Price forcedCurrency={exchangeData.currency} price={{ value: shipping }} />
          </div>
        )}
        <div
          className="total-section pt-2 fw-bold"
          style={{ borderTop: "1px solid var(--bs-border-color)", marginTop: 8 }}
        >
          <span>{t.total_exchange_value || "Exchange Total"}</span>
          <span
            className="total-value-text"
            style={{ color: "var(--bs-primary)", fontSize: "16px" }}
          >
            <Price
              forcedCurrency={exchangeData.currency}
              price={{ value: credit }}
            />
          </span>
        </div>
      </div>

      <Modal
        centered
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
      >
        <div className="p-4">
          <h5 className="mb-3">{t.cancel_exchange_confirmation}</h5>
          <p className="mb-4">{t.cancel_exchange_msg}</p>
          <Row style={{ gap: 10 }}>
            <Col className="p-0">
              <CustomButton
                type={BtnTypes.secondary}
                size={BtnSizes.medium}
                radius={BtnRadius.small}
                onClick={() => setShowCancelModal(false)}
                fullWidth
              >
                {t.back}
              </CustomButton>
            </Col>
            <Col className="p-0">
              <CustomButton
                type={BtnTypes.primary}
                size={BtnSizes.medium}
                radius={BtnRadius.small}
                onClick={handleCancelExchange}
                fullWidth
              >
                {t.confirm}
              </CustomButton>
            </Col>
          </Row>
        </div>
      </Modal>
    </>
  );
};

export default CartExchangeSection;
